import { gql, QueryHookOptions, QueryResult, useQuery } from '@apollo/client';
import type {
  TriageEvidenceDetails,
  EvidenceInput,
} from '@investigator/shared/generated/graphql';

/**
 * Query response type for getTriageEvidenceDetails
 */
export interface IGetTriageEvidenceDetails {
  getTriageEvidenceDetails: TriageEvidenceDetails | null;
}

/**
 * Query variables type
 */
export interface GetTriageEvidenceDetailsVariables {
  tenant: string;
  evidence: EvidenceInput;
}

/**
 * GraphQL query for fetching triage evidence details
 * This query retrieves detailed evidence information for a specific triage finding.
 *
 * @see TriageEvidenceDetails for response schema
 * @see EvidenceInput for input requirements
 */
export const GET_TRIAGE_EVIDENCE_DETAILS = gql`
  query getTriageEvidenceDetails($tenant: String!, $evidence: EvidenceInput!) {
    getTriageEvidenceDetails(tenant: $tenant, evidence: $evidence) {
      evidence
      message
      pivot_query
      truncated
    }
  }
`;

/**
 * Hook for querying triage evidence details
 *
 * @example
 * ```tsx
 * const { data, loading, error } = useGetTriageEvidenceDetails({
 *   variables: {
 *     tenant: 'my-tenant',
 *     evidence: {
 *       agent: 'agent-123',
 *       entity_triage_id: 'triage-456',
 *       source: EvidenceSource.Tool,
 *       tool_name: 'my-tool',
 *       detection_id: 'detection-789', // optional
 *     },
 *   },
 *   skip: !evidenceData,
 * });
 *
 * const evidenceDetails = data?.getTriageEvidenceDetails;
 * ```
 */
export const useGetTriageEvidenceDetails = (
  options: QueryHookOptions<
    IGetTriageEvidenceDetails,
    GetTriageEvidenceDetailsVariables
  >,
): QueryResult<
  IGetTriageEvidenceDetails,
  GetTriageEvidenceDetailsVariables
> => {
  return useQuery<IGetTriageEvidenceDetails, GetTriageEvidenceDetailsVariables>(
    GET_TRIAGE_EVIDENCE_DETAILS,
    options,
  );
};
